import {
  Component,
  OnInit,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { ConnectorsModalComponent } from '../connectors/connectors-modal.component';
import { ConnectorsActions } from '../connectors/connectors.actions';
import * as fromFeatureFlags from '../feature-flags';
import * as fromLayout from '../layout';
import { NEW_CHAT_TITLE_DEFAULT } from './chats.constants';
import { ChatsDesktopResponsive } from './chats.desktop.responsive';
import { ChatsHelper } from './chats.helper';
import { ChatsMobileResponsive } from './chats.mobile.responsive';
import { CHAT_TITLE_PATTERN } from './components/navigation/navigation-chats-list.helper';
import { RouterService } from './shared/services/router.service';
import {
  ChatModalActions,
  ChatsActions,
  ChatsState,
  chatsFeature,
} from './store';
import { hasMessages } from './store/chat-lifecycle.helper';

@Component({
  selector: 'squadbox-chats',
  imports: [
    TranslateModule,
    FormsModule,
    RouterModule,
    ChatsMobileResponsive,
    ChatsDesktopResponsive,
    fromSeriousSystem.ModalComponent,
    fromSeriousSystem.InputComponent,
    ConnectorsModalComponent,
  ],
  providers: [ChatsHelper],
  template: `
    @if (isMobile()) {
      <squadbox-chats-mobile
        [chatNavigationItems]="chatNavigationItems()"
        [isNewChat]="isNewChat()"
      >
      </squadbox-chats-mobile>
    } @else {
      <squadbox-chats-desktop
        [chatNavigationItems]="chatNavigationItems()"
        [isNewChat]="isNewChat()"
        (chatDeleteClicked)="onDeleteChat($event)"
      >
      </squadbox-chats-desktop>
    }
    <sd-modal
      [isModalVisible]="isDeleteChatModalOpened()"
      [modalOptions]="{
        title: translateService.instant('CHAT.DELETE_CHAT_MODAL.TITLE'),
        actions: [
          {
            label: translateService.instant(
              'CHAT.DELETE_CHAT_MODAL.CTA_CANCEL'
            ),
            action: 'cancel',
            type: 'cancel',
            disabled: false,
          },
          {
            label: translateService.instant(
              'CHAT.DELETE_CHAT_MODAL.CTA_DELETE'
            ),
            action: 'delete',
            type: 'delete',
            disabled: false,
          },
        ],
        backdropDismiss: true,
      }"
      (triggerAction)="onTriggerAction($event)"
    >
      <div
        class="w-auto tablet-portrait:w-120"
        innerHTML="{{
          'CHAT.DELETE_CHAT_MODAL.DESCRIPTION'
            | translate: { chatName: chatNameForDeleteModal() }
        }}"
      ></div>
    </sd-modal>
    <sd-modal
      [isModalVisible]="isRenameChatModalOpened()"
      [modalOptions]="{
        title: translateService.instant('CHAT.RENAME_CHAT_MODAL.TITLE'),
        actions: [
          {
            label: translateService.instant(
              'CHAT.RENAME_CHAT_MODAL.CTA_CANCEL'
            ),
            action: 'cancel',
            type: 'cancel',
            disabled: false,
          },
          {
            label: translateService.instant(
              'CHAT.RENAME_CHAT_MODAL.CTA_RENAME'
            ),
            action: 'rename',
            type: 'save',
            disabled: isRenameSubmitDisabled(),
          },
        ],
        backdropDismiss: true,
        dragDismiss: true,
        maxWidth: 358,
        footerClass: 'pt-0',
      }"
      (triggerAction)="onRenameAction($event)"
    >
      <sd-input
        #renameChatInput
        class="block w-full [&_input]:h-14"
        [accessibleLabel]="
          translateService.instant('CHAT.RENAME_CHAT_MODAL.PLACEHOLDER')
        "
        [label]="''"
        [placeholder]="
          translateService.instant('CHAT.RENAME_CHAT_MODAL.PLACEHOLDER')
        "
        [limit]="100"
        [ngModel]="renameValue()"
        (ngModelChange)="renameValue.set($event)"
      />
    </sd-modal>

    <!--
      Hosted here rather than in the composer's menu: it opens from the "+" menu, from the profile
      menu and on the way back from consent, and a modal inside a CDK overlay would close with it.
      Behind ff-mcp-connectors like both entry points, so a ?connector= deep link cannot open it
      either. Its open state lives in the store, so a consent return still shows once the flag loads.
    -->
    @if (ffMcpConnectors()) {
      <squadbox-connectors-modal />
    }
  `,
  styles: [
    `
      @reference '../../styles.css';

      .main-navigation-item {
        @apply grid grid-cols-[30px_auto] items-center align-middle;
        @apply items-center overflow-hidden rounded tablet-landscape:px-6 px-4;
        @apply cursor-pointer hover:bg-primary-500/5;
        @apply [&>svg]:size-5 [&>div]:mr-2;
        @apply [&>div]:size-5 [&>div>img]:size-5;
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class ChatsPage implements OnInit {
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly layoutStore = inject<Store<fromLayout.LayoutState>>(Store);
  private readonly chatsStore = inject<Store<ChatsState>>(Store);

  private readonly routerService = inject(RouterService);
  private readonly chatsHelper = inject(ChatsHelper);

  public readonly translateService = inject(TranslateService);

  public readonly ffMcpConnectors = this.store.selectSignal(
    fromFeatureFlags.featureFlagsFeature.selectMcpConnectors,
  );

  /** LaunchDarkly has evaluated the flags for this user, not just the anonymous boot context. */
  private readonly featureFlagsReady = this.store.selectSignal(
    fromFeatureFlags.featureFlagsFeature.selectReady,
  );

  /** A consent outcome read from the URL, held until `ff-mcp-connectors` says it may be reported. */
  private readonly pendingConnectorReturn = signal<{
    provider: string;
    reason: string | null;
  } | null>(null);

  /**
   * Consent leaves the app entirely and the provider sends the person back here, because this is the
   * route the API redirects to. Hold the outcome for the constructor's effect, then clear the
   * parameters so a refresh does not replay the toast.
   */
  public ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    const provider = params.get('connector');
    if (!provider) {
      return;
    }

    this.pendingConnectorReturn.set({ provider, reason: params.get('error') });
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });
  }

  private readonly chatToDeleteUuid = signal<string | null>(null);
  private readonly renamePreviousTitleRaw = signal(NEW_CHAT_TITLE_DEFAULT);
  private readonly renameChatInput =
    viewChild<fromSeriousSystem.InputComponent>('renameChatInput');
  private renameModalSeeded = false;

  public readonly selectedChatUuid = computed(() => {
    const selectedChat = this.chatsStore.selectSignal(
      chatsFeature.selectSelectedChat,
    )();
    return selectedChat?.uuid ?? null;
  });
  public readonly selectedChatTitle = computed(() => {
    const title = this.chatsStore.selectSignal(
      chatsFeature.selectSelectedChat,
    )()?.title;

    return this.chatsHelper.getChatTitle(title);
  });
  public readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );
  public readonly isDeleteChatModalOpened = this.chatsStore.selectSignal(
    chatsFeature.selectIsDeleteChatModalOpened,
  );
  public readonly isRenameChatModalOpened = this.chatsStore.selectSignal(
    chatsFeature.selectIsRenameChatModalOpened,
  );
  public readonly selectedChat = this.chatsStore.selectSignal(
    chatsFeature.selectSelectedChat,
  );
  public readonly isNewChat = computed(() => {
    const selectedChat = this.selectedChat();
    // An intro-only skill chat, created by a first file, is still new.
    return !!selectedChat && !hasMessages(selectedChat);
  });
  public readonly chatNavigationItems = computed(() => {
    const items = this.chatsStore.selectSignal(
      chatsFeature.selectChatNavigationItems,
      {},
    );

    return items();
  });
  public readonly chatTitleToDelete = computed(() => {
    if (this.chatToDeleteUuid() !== null) {
      const chatToDelete = this.chatNavigationItems().find(
        (item) => item.uuid === this.chatToDeleteUuid(),
      );
      if (chatToDelete) {
        return this.chatsHelper.getChatTitle(chatToDelete.title);
      }
    }
    return this.selectedChatTitle();
  });
  public readonly chatNameForDeleteModal = computed(() =>
    escapeHtml(this.chatTitleToDelete()),
  );
  public readonly renameValue = signal('');
  public readonly isRenameSubmitDisabled = computed(() => {
    const nextTitle = this.renameValue().trim();
    return (
      nextTitle.length === 0 ||
      nextTitle.length > 100 ||
      // Mirror the API DTO's single-line @Matches and
      // @NotEquals(NEW_CHAT_TITLE_DEFAULT) so the commit never 400s and
      // reverts the optimistic update (pasted U+2028/U+2029/NUL survive a
      // single-line input; \r\n do not).
      !CHAT_TITLE_PATTERN.test(nextTitle) ||
      nextTitle === NEW_CHAT_TITLE_DEFAULT ||
      nextTitle === this.chatsHelper.getChatTitle(this.renamePreviousTitleRaw())
    );
  });

  constructor() {
    /**
     * A consent outcome is reported only with the flag on, like the modal it reopens: with connectors
     * off a `?connector=` link must show nothing. Waits for the per-user evaluation rather than
     * reading the flag in `ngOnInit`: until LaunchDarkly identifies the user, the flag holds the
     * client-level value, which can be on for someone whose own targeting turns it off. Once that
     * evaluation lands the outcome is settled either way, so a flag turned on later by streaming
     * cannot replay an old one.
     */
    effect(() => {
      const pending = this.pendingConnectorReturn();
      if (!pending || !this.featureFlagsReady()) {
        return;
      }
      untracked(() => {
        this.pendingConnectorReturn.set(null);
        if (this.ffMcpConnectors()) {
          this.store.dispatch(ConnectorsActions.authorizationReturned(pending));
        }
      });
    });

    effect(() => {
      if (!this.isRenameChatModalOpened()) {
        this.renameModalSeeded = false;
        return;
      }
      if (this.renameModalSeeded) {
        return;
      }

      this.renameModalSeeded = true;
      untracked(() => {
        const chat = this.selectedChat();
        this.renamePreviousTitleRaw.set(chat?.title ?? NEW_CHAT_TITLE_DEFAULT);
        this.renameValue.set(this.chatsHelper.getChatTitle(chat?.title));
        queueMicrotask(() => this.renameChatInput()?.focus());
      });
    });
  }

  public async onTriggerAction(action: string) {
    switch (action) {
      case 'delete': {
        /**
         * Priority 1: If the chatToDeleteUuid is set, delete the chat (this is the case when deleting from navigation dropdown menu)
         * Priority 2: Otherwise, delete the selected chat (this is the case when deleting from mobile topbar)
         */
        const chatUuidToDelete =
          this.chatToDeleteUuid() ?? this.selectedChatUuid();
        if (chatUuidToDelete === this.selectedChatUuid()) {
          await this.routerService.navigateToNewChat();
        }
        if (chatUuidToDelete !== null) {
          // Capture assistantId and the numeric conversationId before dispatch
          // for the conversation_deleted event (SB-3513 T7, SB-3944 / KD-28)
          const chatToDelete = this.chatsStore.selectSignal(
            chatsFeature.selectChatByUuid(chatUuidToDelete),
          )();
          this.chatsStore.dispatch(
            ChatsActions.deleteChat({
              uuid: chatUuidToDelete,
              conversationId: chatToDelete?.id,
              assistantId: chatToDelete?.assistantId ?? undefined,
              startedAsDefaultChat: chatToDelete?.startedAsDefaultChat,
            }),
          );
          this.chatToDeleteUuid.set(null);
        }
        this.chatsStore.dispatch(ChatModalActions.closeDeleteChatModal());
        break;
      }
      case 'cancel':
        this.chatToDeleteUuid.set(null);
        this.chatsStore.dispatch(ChatModalActions.closeDeleteChatModal());
        break;
    }
  }

  public onDeleteChat(chatUuid: string) {
    this.chatToDeleteUuid.set(chatUuid);
    this.chatsStore.dispatch(ChatModalActions.openDeleteChatModal());
  }

  public onRenameAction(action: string) {
    if (action === 'rename' && !this.isRenameSubmitDisabled()) {
      const chatUuid = this.selectedChatUuid();
      if (chatUuid !== null) {
        this.chatsStore.dispatch(
          ChatsActions.renameChat({
            uuid: chatUuid,
            title: this.renameValue().trim(),
            previousTitle: this.renamePreviousTitleRaw(),
            requestId: crypto.randomUUID(),
            renamedAt: new Date().toISOString(),
            source: 'mobile-modal',
          }),
        );
      }
    }

    this.chatsStore.dispatch(ChatModalActions.closeRenameChatModal());
    queueMicrotask(() => {
      document
        .querySelector<HTMLButtonElement>('[data-chat-more-options-trigger]')
        ?.focus();
    });
  }
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
